import { createSelector } from '@reduxjs/toolkit';
import { CUParametersObjAPIResponse } from '../../CUParameters/api/CUParameters.model';
import { TranslationControlUnitObject } from '../../CUTranslation/api/CUTranslation.model';
import { DeviceControlParameterMetadataAPIResponse } from '../../commander/api/device.model';
import { getControlUnitById } from '../../controlUnit/selectors';
import { getCUTranslationParametersFromControlUnit } from '../../CUTranslation/selectors';
import { RootState } from '../../store.model';
import { ParametersAPI } from '../api/ParametersAPI';

export const getCUParametersFromDevice = (
  state: RootState,
  props: { deviceId: string }
) =>
  ParametersAPI.endpoints.getCUParametersFromDevice.select({
    deviceId: props.deviceId,
  })(state).data;

export const getCUParametersFromControlUnit = (
  state: RootState,
  props: { deviceId: string; controlUnitId: string }
) =>
  ParametersAPI.endpoints.getCUParametersFromCU.select({
    deviceId: props.deviceId,
    controlUnitId: props.controlUnitId,
  })(state).data;

export const getCUParametersMetadataFromControlUnit = (
  state: RootState,
  props: { deviceId: string; controlUnitId: string }
) => getControlUnitById(state, props)?.parameters;

const composeParams = (
  parametersMetadata:
    | Record<string, DeviceControlParameterMetadataAPIResponse>
    | undefined,
  parametersValues: CUParametersObjAPIResponse | undefined,
  translationsParameters = {} as Record<
    string,
    TranslationControlUnitObject | undefined
  >
) => {
  return Object.entries(parametersMetadata ?? {}).map(
    ([key, parameterMetadataLevel]) => ({
      id: key,
      titleTranslation:
        translationsParameters?.[key]?.longText ??
        translationsParameters?.[key]?.shortText,
      values: parameterMetadataLevel.parameters.map((parameterMetadata) => ({
        ...parameterMetadata,
        value: parametersValues?.parameters?.[key]?.find(
          (parameterValue) => parameterValue.name === parameterMetadata.name
        )?.value,
        titleTranslation:
          translationsParameters?.[parameterMetadata.name]?.shortText,
        descriptionTranslation:
          translationsParameters?.[parameterMetadata.name]?.longText,
        selections:
          translationsParameters?.[parameterMetadata.name]?.selections,
        bits: translationsParameters?.[parameterMetadata.name]?.bits,
        level: key,
      })),
    })
  );
};

export type ComposedParameters = ReturnType<typeof composeParams>;
export type ComposedParameterLevel = ComposedParameters[0];
export type ComposedParametersValue = ComposedParameters[0]['values'][0];

export const getCUParametersComposedFromControlUnit = createSelector(
  [
    getCUParametersMetadataFromControlUnit,
    getCUParametersFromControlUnit,
    getCUTranslationParametersFromControlUnit,
  ],
  composeParams
);

export const getLoadingCUParametersFromControlUnit = (
  state: RootState,
  props: { deviceId: string; controlUnitId: string }
) => {
  return ParametersAPI.endpoints.getCUParametersFromCU.select({
    deviceId: props.deviceId,
    controlUnitId: props.controlUnitId,
  })(state).isLoading;
};

export const getErrorCUParametersFromControlUnit = (
  state: RootState,
  props: { deviceId: string; controlUnitId: string }
) => {
  return ParametersAPI.endpoints.getCUParametersFromCU.select({
    deviceId: props.deviceId,
    controlUnitId: props.controlUnitId,
  })(state).error;
};
